import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last Updated: May 2026</p>

        <section style={styles.section}>
          <h2 style={styles.heading}>1. Introduction</h2>
          <p style={styles.text}>
            Sytch LLC ("Sytch," "we," "us," or "our") operates a peer-to-peer
            ridesharing platform accessible at sytch.app. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your information when you use our
            platform. Please read this policy carefully. If you disagree with its terms,
            please discontinue use of the platform.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>2. Information We Collect</h2>
          <p style={styles.text}>We collect the following types of information:</p>
          <p style={styles.text}><strong>Personal Information:</strong> Name, email address,
          phone number, and profile photo provided during registration.</p>
          <p style={styles.text}><strong>Identity Verification:</strong> Government-issued ID
          and background check information processed through our third-party provider, Checkr, Inc.</p>
          <p style={styles.text}><strong>Payment Information:</strong> Payment card details
          processed securely through Stripe. Sytch does not store your full card number.</p>
          <p style={styles.text}><strong>Trip Information:</strong> Origin, destination,
          departure times, and trip history.</p>
          <p style={styles.text}><strong>Communications:</strong> Messages sent between
          users through our in-app messaging system.</p>
          <p style={styles.text}><strong>Usage Data:</strong> Device information, IP address,
          browser type, and how you interact with our platform.</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>3. How We Use Your Information</h2>
          <p style={styles.text}>We use your information to:</p>
          <p style={styles.text}>• Provide, operate, and maintain the Sytch platform</p>
          <p style={styles.text}>• Verify your identity and conduct background checks</p>
          <p style={styles.text}>• Process payments and prevent fraud</p>
          <p style={styles.text}>• Facilitate connections between drivers and riders</p>
          <p style={styles.text}>• Send transactional communications including booking
          confirmations and verification codes via SMS and email</p>
          <p style={styles.text}>• Comply with legal obligations</p>
          <p style={styles.text}>• Improve and develop our platform</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>4. How We Share Your Information</h2>
          <p style={styles.text}>We do not sell your personal information. We share
          information only in the following circumstances:</p>
          <p style={styles.text}><strong>With other users:</strong> Your first name,
          profile photo, average rating, and verification status are visible to other
          users when you post or book trips.</p>
          <p style={styles.text}><strong>With service providers:</strong> We share
          information with Checkr (background checks), Stripe (payments), Twilio (SMS),
          and SendGrid (email) solely to provide our services.</p>
          <p style={styles.text}><strong>For legal reasons:</strong> We may disclose
          information when required by law or to protect the rights and safety of
          Sytch and its users.</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>5. Background Checks</h2>
          <p style={styles.text}>
            Sytch uses Checkr, Inc. to conduct background checks on all users.
            Background check information is governed by the Fair Credit Reporting Act (FCRA).
            You have rights under the FCRA, including the right to know what is in your
            report and to dispute inaccurate information. Sytch does not share your
            background check report with other users - only the resulting verification
            status (verified or not verified) is displayed publicly.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>6. SMS Communications</h2>
          <p style={styles.text}>
            By providing your phone number and verifying it on our platform, you consent
            to receive SMS messages from Sytch for account verification and
            transactional notifications. Message and data rates may apply. You can opt
            out at any time by replying STOP to any SMS message.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>7. Data Retention</h2>
          <p style={styles.text}>
            We retain your personal information for as long as your account is active
            or as needed to provide services. You may request deletion of your account
            by contacting us at support@sytch.app. Note that some information may
            be retained for legal compliance purposes.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>8. Security</h2>
          <p style={styles.text}>
            We implement industry-standard security measures including encryption,
            secure HTTPS connections, and password hashing. However, no method of
            transmission over the internet is 100% secure, and we cannot guarantee
            absolute security.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>9. Children's Privacy</h2>
          <p style={styles.text}>
            Sytch is not intended for users under 18 years of age. We do not
            knowingly collect personal information from minors. If we become aware
            that a minor has provided us with personal information, we will delete it.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>10. Your Rights</h2>
          <p style={styles.text}>
            Depending on your location, you may have rights regarding your personal
            information including the right to access, correct, or delete your data.
            To exercise these rights, contact us at support@sytch.app.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>11. Changes to This Policy</h2>
          <p style={styles.text}>
            We may update this Privacy Policy from time to time. We will notify you
            of material changes via email or a prominent notice on our platform.
            Continued use of Sytch after changes constitutes acceptance of
            the updated policy.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.heading}>12. Contact Us</h2>
          <p style={styles.text}>
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <p style={styles.text}>
            Sytch LLC<br />
            Email: support@sytch.app<br />
            Website: sytch.app
          </p>
        </section>
      </div>
    </div>
  );
};

const styles = {
  container: {
    background: '#f0f2f5',
    minHeight: '100vh',
    padding: '40px 20px',
    fontFamily: 'Arial, sans-serif',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '16px',
    padding: '48px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  lastUpdated: {
    color: '#999',
    fontSize: '14px',
    margin: '0 0 40px 0',
  },
  section: {
    marginBottom: '32px',
  },
  heading: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 12px 0',
  },
  text: {
    color: '#555',
    lineHeight: '1.7',
    margin: '0 0 12px 0',
    fontSize: '15px',
  },
};

export default PrivacyPolicy;